// Precompiled, module-level regexes so the hot error-tracking path never allocates a regex
// per call. String.replace resets a global regex's lastIndex, so reusing these is safe.

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// "...address 'hlb6599@yahoo.com'..." -> "...address '{email}'...". Applicant emails are unique
// per error, so masking them keeps otherwise-identical validation messages grouped.
const EMAIL_RE = /[^\s@'"]+@[^\s@'"]+\.[^\s@'"]+/g;

// "<keyword> 7120176" / "order #4567" / "id=12345" / "Loan No7165994" / "tenant 914486820" ->
// "<keyword> {id}". The keyword anchors the match so unrelated numbers (counts, versions) are
// left alone. The optional "no"/"no." token covers "Loan No1234"-style ids with no separator.
const KEYWORD_ID_RE =
    /\b(number|id|loan|account|reference|ref|order|invoice|application|tenant)(\s*(?:no\.?\s*)?[:#=]?\s*)\d{2,}/gi;

// "businessUnit:129" / "tenantId:4603963931" -> "businessUnit:{id}". A word immediately followed by
// a colon and digits is always a key:id pair, so a 2-digit floor is safe here. Requiring a leading
// letter skips clock times ("12:30") and error-code prefixes ("1018:The Merchant...").
const WORD_COLON_ID_RE = /\b([a-zA-Z]\w*:)\d{2,}/gi;

// "<label>: 7120176" -> "<label>: {id}". A space after the colon means this is not a key:id pair
// (those are handled above), so the 4-digit floor preserves HTTP codes like ": 502".
const COLON_ID_RE = /(:\s+)\d{4,}/g;

// Below this length a message cannot hold a maskable identifier, so we skip the work entirely.
const MIN_NORMALIZE_LENGTH = 4;

// A long unbroken token (raw HTML body, JSON/stack blob) makes EMAIL_RE's overlapping runs scan
// superlinearly, and such messages are inherently high-cardinality, so templating them would not
// group anything. We skip them to keep this per-error path bounded.
const MAX_NORMALIZE_LENGTH = 2000;

/**
 * Collapses dynamic identifiers in an error message into stable placeholders so FullStory can
 * group otherwise-unique messages. Masks GUIDs, emails, and context-anchored numeric IDs only;
 * static text such as provider names and HTTP status codes is preserved. Runs on every tracked
 * error, so it stays a single linear pass of precompiled replacements.
 */
export function normalizeErrorMessage(message: string): string {
    if (
        !message ||
        message.length < MIN_NORMALIZE_LENGTH ||
        message.length > MAX_NORMALIZE_LENGTH
    ) {
        return message ?? '';
    }

    return message
        .replace(GUID_RE, '{guid}')
        .replace(EMAIL_RE, '{email}')
        .replace(KEYWORD_ID_RE, '$1$2{id}')
        .replace(WORD_COLON_ID_RE, '$1{id}')
        .replace(COLON_ID_RE, '$1{id}');
}
